/**
 * Utility functions for downloading the bubble chart with watermark
 */

import { Chart as ChartJS } from "chart.js";
import { parseServerDate } from "./formatters";

interface DownloadChartOptions {
  chartRef: ChartJS; // Chart.js chart instance
  filename: string;
  watermarkLogoPath?: string;
}

/**
 * Load an SVG as an image and return a promise with the image element
 */
const loadSvgAsImage = (svgPath: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = svgPath;
  });
};

/**
 * Download the chart as PNG with watermark
 */
export const downloadChartWithWatermark = async ({
  chartRef,
  filename,
  watermarkLogoPath = "/clusterview-logo-with-text.svg",
}: DownloadChartOptions): Promise<void> => {
  if (!chartRef) {
    console.error("Chart reference not available");
    return;
  }

  try {
    // Re-render the chart at a higher device pixel ratio so the exported graph is as crisp as the vector logo. 
    // Bumping devicePixelRatio makes Chart.js redraw everything at full resolution instead of magnifying a low-res bitmap.
    const EXPORT_PIXEL_RATIO = 4;
    const originalDevicePixelRatio = chartRef.options.devicePixelRatio;
    let chartBase64 = "";
    try {
      chartRef.options.devicePixelRatio = EXPORT_PIXEL_RATIO;
      // resize() rescales the backing store; draw() repaints it synchronously so the high-res pixels exist before we capture them.
      chartRef.resize();
      chartRef.draw();
      chartBase64 = chartRef.toBase64Image("image/png", 1);
    } finally {
      // Always restore the on-screen chart to its original resolution.
      chartRef.options.devicePixelRatio = originalDevicePixelRatio;
      chartRef.resize();
      chartRef.draw();
    }

    // Create a new canvas for compositing
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get canvas context");
    }

    // Load the (high-resolution) chart image
    const chartImg = new Image();
    await new Promise<void>((resolve, reject) => {
      chartImg.onload = () => resolve();
      chartImg.onerror = reject;
      chartImg.src = chartBase64;
    });

    // Load the logo up front so we can size the header band around it.
    let logoImg: HTMLImageElement | null = null;
    try {
      logoImg = await loadSvgAsImage(watermarkLogoPath);
    } catch (error) {
      console.warn("Could not load logo, proceeding without it:", error);
    }

    // Layout: the graph sits in a white card, framed by a soft lavender border that carries the brand colours. 
    // The logo lives in the top padding strip, in the upper-right corner, fully outside the graph.
    const chartW = chartImg.width;
    const chartH = chartImg.height;
    const gap = chartW * 0.03; // base spacing unit, scales with resolution

    const logoWidth = chartW * 0.13;
    const logoHeight = logoImg
      ? logoWidth * (logoImg.height / logoImg.width)
      : 0;

    const sidePad = gap;
    const headerH = logoHeight + gap * 2; // top strip that holds the logo
    const bottomPad = gap;

    canvas.width = chartW + sidePad * 2;
    canvas.height = headerH + chartH + bottomPad;

    // Use high-quality smoothing for the logo scaling.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Soft lavender frame, drawn from the brand gradient.
    ctx.fillStyle = "#F3F0FF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // White card that holds the graph, with a subtle shadow so it lifts off the lavender frame.
    const cardX = sidePad;
    const cardY = headerH;
    const cardRadius = gap * 0.6;
    ctx.save();
    ctx.shadowColor = "rgba(48, 37, 58, 0.16)";
    ctx.shadowBlur = gap * 0.7;
    ctx.shadowOffsetY = gap * 0.2;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(cardX, cardY, chartW, chartH, cardRadius);
    ctx.fill();
    ctx.restore();

    // Draw the graph 1:1 (no upscaling) inside the white card.
    ctx.drawImage(chartImg, cardX, cardY, chartW, chartH);

    // Draw the logo in the top padding strip, upper-right, at full opacity.
    if (logoImg) {
      const logoX = canvas.width - sidePad - logoWidth;
      const logoY = (headerH - logoHeight) / 2;
      ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
    }

    // Convert canvas to blob and download
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error("Could not create blob from canvas");
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, "image/png");
  } catch (error) {
    console.error("Error downloading chart:", error);
    throw error;
  }
};

/**
 * Generate a filename for the chart download based on cluster metadata
 */
export const generateChartFilename = (
  anchorKeyword: string,
  languageCode: string,
  locationCode: string | number,
  fetchDate?: string | null,
  format: "jpeg" | "pdf" = "jpeg"
): string => {
  // Sanitize anchor keyword for filename (remove special characters, limit length)
  const sanitizedKeyword = anchorKeyword
    .replace(/[^a-zA-Z0-9-_ ]/g, "")
    .replace(/\s+/g, "_")
    .substring(0, 30);

  // Format date as YYYY-MM-DD in the browser's local timezone (not UTC), so the stamp matches the day the user perceives rather than the UTC calendar day.
  const pad = (n: number): string => String(n).padStart(2, "0");
  const toLocalDateStr = (d: Date): string =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  let date = fetchDate ? parseServerDate(fetchDate) : new Date();
  if (isNaN(date.getTime())) {
    date = new Date(); // Fall back to today if fetchDate is unparseable.
  }
  const dateStr = toLocalDateStr(date);

  const ext = format === "pdf" ? "pdf" : "jpg";
  return `[ClusterView ${dateStr}] ${sanitizedKeyword}  lang-${languageCode} loc-${locationCode}.${ext}`;
};

// downloadChartWithWatermark is kept above for backward compatibility. Use exportChart from chartExport.ts for the full-layout export.
